import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { DossierSummaryResponseDto } from 'lib_api/lib/api/gen/api';

import { baseOptionTypeArrayToStringArray } from 'components/BaseForm/utils';
import { Table } from 'components/WrappedComponents';
import {
  ColumnProps,
  FetchDataFunction,
} from 'components/WrappedComponents/Table/types';
import { useSearchDossierSummaries } from 'hooks/dossiers/useSearchDossierSummaries';
import { useJusticeFilterContext } from 'hooks/JusticeFilterStoreContext';
import { usePaginationContext } from 'hooks/PaginationStoreContext';
import { useSortContext } from 'hooks/SortStoreContext';
import { DEFAULT_PAGE_SIZE_OPTIONS } from 'utils/global';

import { DashboardJusticeFilter } from '../types';

import { JusticeSortOptions } from './types';
import { buildColumns, buildHeader } from './utils';

import './DashboardTable.less';

function DashboardTable(): React.ReactElement {
  const { pathname } = useLocation();
  const [sort, setSort] = useSortContext();
  const [filter, setFilter, justiceActionsRequises] = useJusticeFilterContext();
  const searchDossierSummaries = useSearchDossierSummaries();
  const [pagination, setPagination] = usePaginationContext('paginationDossier');

  const fetchData: FetchDataFunction<
    DossierSummaryResponseDto,
    DashboardJusticeFilter,
    JusticeSortOptions
  > = useMemo(
    () => (pagination, sorters, filter) => {
      return searchDossierSummaries(pagination, sorters, {
        dateRangeMiseEnFourriere: filter.dateRangeMiseEnFourriere,
        idNatureBrancheList: filter.natureMiseEnFourriere,
        statutDossierEnumList: filter.statut,
        idFourriereList: baseOptionTypeArrayToStringArray(filter.fourriere),
        actionsRequises: filter.actionsRequises,
        numeroRecherche: filter.search,
        includeDossiersSortis: filter.includeDossiersSortis,
      });
    },
    [searchDossierSummaries],
  );

  const columns: ColumnProps<DossierSummaryResponseDto, JusticeSortOptions>[] =
    useMemo(
      () => buildColumns(pathname, justiceActionsRequises),
      [pathname, justiceActionsRequises],
    );

  return (
    <div className="table-container">
      <Table
        header={buildHeader}
        columns={columns}
        fetchData={fetchData}
        buildItemKey={(item): string => item.id}
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        pagination={pagination}
        setPagination={setPagination}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
      />
    </div>
  );
}

export default DashboardTable;
